/**
 * MOBA-style progression. Everyone enters at level 1 and levels inside the
 * match; nothing carries between matches, so there is no grind advantage.
 *
 * The original needed 3.53 million experience for level 30 across months of
 * play. This curve keeps that shape — fast early, slow late — compressed into
 * a single twelve-minute match.
 */

import type { SlotRole } from './spells.ts';

export const MAX_LEVEL = 30;

/**
 * Cumulative experience to *reach* each level, written out rather than derived.
 *
 * The generated curve it replaces was too generous early — level 8 cost 357,
 * which a mage reached on damage alone in the first skirmish, so most of the
 * spell ladder arrived before anyone had done anything. This one costs 875 for
 * the same level and 9,001 for thirty, against 4,000 before.
 *
 * It is neither linear nor a clean exponential: the steps grow by 25 through the
 * early levels, flatten to a near-constant 250-350 through the middle where most
 * of a match is actually played, and steepen again at the top so that level 30
 * stays something a good match produces rather than something every match does.
 * A formula that fit all three stretches would be harder to read and much harder
 * to tune, and this table is going to be tuned again.
 */
export const XP_TABLE: readonly number[] = [
  0, 50, 125, 225, 350, 500, 675, 875, 1100, 1350,
  1600, 1850, 2125, 2400, 2675, 2975, 3275, 3575, 3900, 4225,
  4550, 4900, 5250, 5600, 6000, 6450, 6950, 7500, 8100, 9001,
];

/** Cumulative experience required to reach the given level. */
export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  return XP_TABLE[Math.min(MAX_LEVEL, level) - 1];
}

export function levelForXp(xp: number): number {
  let level = 1;
  for (let l = MAX_LEVEL; l >= 1; l--) {
    if (xp >= XP_TABLE[l - 1]) {
      level = l;
      break;
    }
  }
  return level;
}

/** Progress toward the next level, 0..1. Returns 1 at max level. */
export function levelProgress(xp: number): number {
  const level = levelForXp(xp);
  if (level >= MAX_LEVEL) return 1;
  const from = XP_TABLE[level - 1];
  const to = XP_TABLE[level];
  return (xp - from) / (to - from);
}

/** Experience awards. Damage is the dominant source, as in the original. */
export const XP_AWARDS = {
  perDamageDealt: 1,
  killBonus: 25,
  poolBiased: 15,
  shrineConquered: 250,
  matchWin: 400,
  /**
   * For raising a teammate. Modest against a kill, on purpose: it should be
   * worth breaking off for, not worth farming, and a Cleric who spends the match
   * pulling people up should still fall behind one who fights.
   */
  raiseBonus: 20,
} as const;

// ── Spell tiers ──────────────────────────────────────────────────────────────
/**
 * Levelling upgrades the tier of spells already in your loadout rather than
 * handing you new ones. Wounding I becomes Wounding II becomes Wounding III,
 * and you never open a menu mid-fight.
 */
export const MAX_TIER = 5;

/**
 * When each slot's tiers arrive, shared by every class and every spell in that
 * slot.
 *
 * Two things changed at once here. The schedule is now per *slot* rather than
 * global, so a mage gains roughly one upgrade a level instead of watching all
 * six slots step up together on four occasions; and the six slots no longer all
 * start at level 1.
 *
 * Only Primary, Defense and Control are available at the outset. Utility arrives
 * at 2, Secondary at 3 and Area at 5, which is the direct answer to a level-1
 * Wounding Triad averaging 10.5 against a 25-hit-point Magician — nearly half a
 * health bar from a spell nobody has earned yet. Primaries deal four to six and
 * are fine; it was always the burst slots that were outsized at the bottom.
 */
export const SLOT_UNLOCK_LEVELS: Record<SlotRole, readonly number[]> = {
  // Innates carry their own schedule on the spell; this is only a fallback for
  // anything that forgets to.
  innate: [1, 7, 14, 21, 28],
  primary: [1, 6, 11, 17, 23],
  defense: [1, 7, 13, 18, 23],
  control: [1, 8, 15, 20, 26],
  utility: [2, 10, 16, 21, 27],
  secondary: [3, 10, 16, 22, 29],
  area: [5, 11, 17, 24, 30],
};

/**
 * Tier of a slot at a level: 1..5, or **0 when the slot is not yet available**.
 *
 * Zero is a genuinely new state. Until now every spell in a loadout could be
 * cast from the first second of a match and only got stronger; an Area spell
 * that arrives at level 5 means a slot that is carried but cannot be used, which
 * every caller has to handle rather than clamping to tier I and firing anyway.
 */
export function tierForSlot(slot: SlotRole, level: number): number {
  const schedule = SLOT_UNLOCK_LEVELS[slot];
  let tier = 0;
  for (let i = 0; i < schedule.length; i++) {
    if (level >= schedule[i]) tier = i + 1;
  }
  return tier;
}

/**
 * Tier of a specific spell, which is the slot's schedule unless the spell has
 * one of its own.
 *
 * The class innates are not carried in a slot, so they have nothing to inherit:
 * Resurrect climbs at 1, 7, 14, 21 and 28, and Call Ghost arrives at 2 and stays
 * where it is.
 */
export function tierForSpell(
  spell: { slot: SlotRole; unlockLevels?: readonly number[] }, level: number,
): number {
  const schedule = spell.unlockLevels ?? SLOT_UNLOCK_LEVELS[spell.slot];
  let tier = 0;
  for (let i = 0; i < schedule.length; i++) {
    if (level >= schedule[i]) tier = i + 1;
  }
  return tier;
}

/** The level at which a slot becomes usable at all. */
export function slotUnlockLevel(slot: SlotRole): number {
  return SLOT_UNLOCK_LEVELS[slot][0];
}

/**
 * Experience lost by taking the walk to Valhalla.
 *
 * A third of the progress made *since the last level*, which has the property
 * that matters: it can never cost a level. Taking the long way home should be a
 * setback you feel and not a punishment that undoes the last ten minutes — the
 * old rule took a quarter of everything earned in the match, which at level
 * fourteen could drop somebody to twelve and take their spells with it.
 *
 * Somebody who has just levelled loses nothing at all, which is right: they have
 * no progress to give back.
 */
export const VALHALLA_PROGRESS_LOSS = 1 / 3;

export function valhallaPenalty(xp: number): number {
  const level = levelForXp(xp);
  const floor = XP_TABLE[level - 1];
  return Math.floor((xp - floor) * VALHALLA_PROGRESS_LOSS);
}

export const ROMAN = ['I', 'II', 'III', 'IV', 'V'] as const;
